import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SafeAreaService } from '../../@services/webview/safe-area.service';
import { NativeBridgeService } from '../../@services/webview/native-bridge.service';
import { AlertCategoryIconComponent } from '../../../@icons/alert-category-icon.component';

/** Push reçu app ouverte, tel que la coque le transmet (`onPushReceived`). */
export interface ForegroundPush {
  title?: string | null;
  body?: string | null;
  data?: Record<string, string>;
}

/** Durée d'affichage avant retrait automatique. */
export const PUSH_BANNER_DURATION_MS = 6_000;

/** Glissé vers le haut, en pixels, qui ferme la bannière. */
const SWIPE_CLOSE_PX = 24;

/**
 * Bannière d'une alerte reçue pendant que l'app est ouverte.
 *
 * Ni iOS ni Android n'affichent un push pour l'app au premier plan : avant la
 * 1.5.1, l'alerte « gel cette nuit » arrivait pendant qu'on regardait son
 * potager et personne ne la voyait. La coque la transmet ici
 * (`window.onPushReceived`), on la montre 6 s en haut de l'écran, « Voir »
 * ouvre l'écran de l'alerte, un glissé vers le haut la ferme.
 *
 * Un binaire plus ancien n'appelle jamais `onPushReceived` : rien ne change.
 */
@Component({
  selector: 'vw-push-banner',
  imports: [TranslatePipe, AlertCategoryIconComponent],
  templateUrl: './push-banner.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PushBannerComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly safeAreaService = inject(SafeAreaService);
  private readonly bridge = inject(NativeBridgeService);

  readonly current = signal<ForegroundPush | null>(null);
  /** Encoche haute remontée par la coque, en pixels. */
  readonly safeTop = signal(0);

  private timer?: ReturnType<typeof setTimeout>;
  private touchStartY: number | null = null;
  private safeAreaSubscription?: Subscription;
  private stopListening?: () => void;

  ngOnInit(): void {
    this.stopListening = this.bridge.listen('onPushReceived', (raw: unknown) =>
      this.show(parsePush(raw)),
    );
    this.safeAreaSubscription = this.safeAreaService.safeAreaData$.subscribe(
      (data) => this.safeTop.set(data?.top ?? 0),
    );
  }

  show(push: ForegroundPush | null): void {
    if (!push || (!push.title && !push.body)) return;
    this.current.set(push);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.dismiss(), PUSH_BANNER_DURATION_MS);
  }

  dismiss(): void {
    clearTimeout(this.timer);
    this.current.set(null);
  }

  /** « Voir » : l'écran de l'alerte. Son `alertUid` attribue l'ouverture. */
  view(): void {
    const deepLink = this.current()?.data?.['deepLink'];
    this.dismiss();
    const path = toAppPath(deepLink);
    void this.router.navigateByUrl(path ?? '/notifications');
  }

  /** « Urgent » ou « À surveiller », mêmes mots que les notifications (K30). */
  get severity(): 'urgent' | 'watch' | null {
    switch (this.current()?.data?.['severity']) {
      case 'CRITICAL':
        return 'urgent';
      case 'WARNING':
        return 'watch';
      default:
        return null;
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartY = event.touches[0]?.clientY ?? null;
  }

  onTouchEnd(event: TouchEvent): void {
    const endY = event.changedTouches[0]?.clientY;
    if (this.touchStartY !== null && endY !== undefined) {
      if (this.touchStartY - endY > SWIPE_CLOSE_PX) this.dismiss();
    }
    this.touchStartY = null;
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
    this.safeAreaSubscription?.unsubscribe();
    this.stopListening?.();
  }
}

/** Lit la charge de la coque : une CHAÎNE JSON (ou un objet, par tolérance). */
function parsePush(raw: unknown): ForegroundPush | null {
  try {
    const push = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return push && typeof push === 'object' ? (push as ForegroundPush) : null;
  } catch {
    console.warn('onPushReceived : charge illisible', raw);
    return null;
  }
}

/**
 * Chemin interne d'un lien d'alerte. Le back envoie un chemin relatif
 * (`/garden/…?alertUid=…`) ; une URL complète est ramenée à son chemin pour ne
 * jamais quitter l'app.
 */
function toAppPath(deepLink: string | undefined): string | null {
  if (!deepLink) return null;
  if (deepLink.startsWith('/')) return deepLink;
  try {
    const url = new URL(deepLink);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}
